import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useCallback, useEffect } from 'react';
import { ModalWindow, useLocalizeMessage } from 'libs.nucleus';
import { AddEditMeasureStep1 } from './add_edit_measure_step_1.component';
import { AddEditMeasureStep2 } from './add_edit_measure_step_2.component';
import { StepFooter } from './step_footer.component';
import { useStepNavigation } from './use_step_navigation.hook';
import { getMeasureDraftError, validateMeasureDrafts } from '../../utils';
const TOTAL_STEPS = 2;
const EMPTY_MEASURE_FORM = {
    name: '',
    description: '',
    calculationType: 'DROPDOWN',
    type: 'number',
};
/** Appends the entry, or replaces the one being re-edited when `index` is set. */
const foldAtIndex = (entries, entry, index) => {
    if (index === null) {
        return [...entries, entry];
    }
    return entries.map((existing, position) => (position === index ? entry : existing));
};
export const AddEditMeasureModal = ({ isOpen, onClose, onSave, onSaveMany, dataView, isLoading = false, isEditMode = false, editingMeasureId = null, isReadOnly = false, }) => {
    const translate = useLocalizeMessage();
    // Parse SQL to extract function and dimension if it matches the predefined pattern "function(dimension)"
    // This is used as a fallback for legacy measures that don't have meta.isPredefined
    const parseMeasureSQL = useCallback((sql) => {
        if (!sql) {
            return null;
        }
        // Pattern: function_name(dimension_value) or function_name(DISTINCT dimension_value)
        const pattern = /^([a-z]+)\((?:distinct\s+)?(?:distinct\s*\()?\s*([^)]+?)\s*\)?\)$/i;
        const match = sql.trim().match(pattern);
        if (match) {
            const [, functionName, dimensionValue] = match;
            const functionMap = {
                count: 'Count',
                sum: 'Sum',
                avg: 'Average',
                average: 'Average',
                min: 'Min',
                max: 'Max',
            };
            const capitalizedFunction = functionMap[functionName.toLowerCase()];
            if (capitalizedFunction) {
                return {
                    function: capitalizedFunction,
                    dimension: dimensionValue.trim(),
                };
            }
        }
        return null;
    }, []);
    // Load existing measure data when editing
    const existingMeasure = useMemo(() => {
        if (!isEditMode || !editingMeasureId || !dataView.data?.measures?.[editingMeasureId]) {
            return null;
        }
        const measure = dataView.data.measures[editingMeasureId];
        const sql = measure.sql || '';
        // Check meta.isPredefined flag first, fallback to SQL parsing for legacy measures
        const isPredefined = measure.meta?.isPredefined;
        if (isPredefined === true) {
            // Use the isPredefined flag - parse SQL to extract function and dimension for dropdown mode
            const parsed = parseMeasureSQL(sql);
            return {
                name: measure.title || editingMeasureId,
                description: measure.description || '',
                calculationType: 'DROPDOWN',
                sql,
                measureFunction: parsed?.function,
                dimension: parsed?.dimension,
                type: measure.type || 'number',
            };
        }
        else if (isPredefined === false) {
            // Explicitly set to SQL editor mode
            return {
                name: measure.title || editingMeasureId,
                description: measure.description || '',
                calculationType: 'SQL',
                sql,
                measureFunction: undefined,
                dimension: undefined,
                type: measure.type || 'number',
            };
        }
        // Fallback for legacy measures without meta.isPredefined: try parsing SQL
        const parsed = parseMeasureSQL(sql);
        if (parsed) {
            return {
                name: measure.title || editingMeasureId,
                description: measure.description || '',
                calculationType: 'DROPDOWN',
                sql,
                measureFunction: parsed.function,
                dimension: parsed.dimension,
                type: measure.type || 'number',
            };
        }
        // Default to SQL editor mode
        return {
            name: measure.title || editingMeasureId,
            description: measure.description || '',
            calculationType: (sql ? 'SQL' : 'DROPDOWN'),
            sql,
            measureFunction: undefined,
            dimension: undefined,
            type: measure.type || 'number',
        };
    }, [isEditMode, editingMeasureId, dataView.data?.measures, parseMeasureSQL]);
    const [formData, setFormData] = useState(() => {
        if (existingMeasure) {
            return existingMeasure;
        }
        return EMPTY_MEASURE_FORM;
    });
    // Track whether the query has been run successfully in step 2
    const [isStep2QueryValid, setIsStep2QueryValid] = useState(false);
    const [drafts, setDrafts] = useState([]);
    const [draftErrors, setDraftErrors] = useState([]);
    const [editingDraftIndex, setEditingDraftIndex] = useState(null);
    // Update form data when existing measure changes
    useEffect(() => {
        if (existingMeasure) {
            setFormData(existingMeasure);
        }
        else {
            setFormData(EMPTY_MEASURE_FORM);
        }
    }, [existingMeasure]);
    /** Whether more measures can be queued alongside the one being configured. */
    const canQueueMany = !isEditMode && !isReadOnly && !!onSaveMany;
    const nameError = useMemo(() => {
        // Edit and view modes reuse the stored title with the field disabled, so there is nothing to validate.
        if (isEditMode || isReadOnly || !formData.name?.trim()) {
            return '';
        }
        return getMeasureDraftError({ name: formData.name }, dataView, drafts, translate, editingDraftIndex ?? undefined);
    }, [formData.name, dataView, drafts, editingDraftIndex, isEditMode, isReadOnly, translate]);
    const isStep1Valid = useMemo(() => {
        return !!formData.name?.trim() && !nameError;
    }, [formData.name, nameError]);
    /**
     * A queued measure was Run-verified before it was banked, so re-opening it to rename or
     * re-describe it does not need another run. Only a change to its calculation does.
     */
    const isQueuedCalculationUnchanged = useMemo(() => {
        if (editingDraftIndex === null) {
            return false;
        }
        const draft = drafts[editingDraftIndex];
        if (!draft) {
            return false;
        }
        return (formData.calculationType === draft.calculationType &&
            (formData.sql || '') === (draft.sql || '') &&
            (formData.measureFunction || '') === (draft.measureFunction || '') &&
            (formData.dimension || '') === (draft.dimension || '') &&
            (formData.type || 'number') === (draft.type || 'number'));
    }, [drafts, editingDraftIndex, formData]);
    /**
     * The measure currently being configured, or null while it cannot be committed yet.
     * Every calculation is Run-verified in step 2 before it can be queued or committed: the
     * preview is the only proof it resolves against the dataset.
     */
    const currentDraft = useMemo(() => {
        const name = formData.name?.trim();
        if (!name || nameError || !formData.calculationType || !(isStep2QueryValid || isQueuedCalculationUnchanged)) {
            return null;
        }
        const hasCalculation = formData.calculationType === 'SQL' ? !!formData.sql?.trim() : !!formData.measureFunction && !!formData.dimension;
        if (!hasCalculation) {
            return null;
        }
        return {
            name,
            description: formData.description,
            calculationType: formData.calculationType,
            sql: formData.sql,
            measureFunction: formData.measureFunction,
            dimension: formData.dimension,
            type: formData.type || 'number',
        };
    }, [formData, nameError, isStep2QueryValid, isQueuedCalculationUnchanged]);
    /** Banked names, stable while a rename is in progress, so queued rows keep their identity. */
    const draftKeys = useMemo(() => drafts.map((draft) => draft.name), [drafts]);
    /**
     * The queue as shown to the user: the row being edited reflects the in-progress form, so
     * the list is never a stale copy of what is about to be committed.
     */
    const displayDrafts = useMemo(() => {
        if (editingDraftIndex === null) {
            return drafts;
        }
        return drafts.map((draft, position) => {
            if (position !== editingDraftIndex) {
                return draft;
            }
            return {
                ...draft,
                name: formData.name?.trim() || draft.name,
                description: formData.description,
                calculationType: formData.calculationType || draft.calculationType,
                sql: formData.sql,
                measureFunction: formData.measureFunction,
                dimension: formData.dimension,
                type: formData.type || draft.type,
            };
        });
    }, [drafts, editingDraftIndex, formData]);
    const stepValidations = useMemo(() => ({
        1: () => isStep1Valid,
    }), [isStep1Valid]);
    const { currentStep, isFirstStep, isLastStep, handleNext, handlePrevious, goToStep } = useStepNavigation({
        totalSteps: TOTAL_STEPS,
        stepValidations,
    });
    const handleFormChange = useCallback((data) => {
        setFormData((prev) => ({ ...prev, ...data }));
    }, []);
    const handleQueryValidationChange = useCallback((isValid) => {
        setIsStep2QueryValid(isValid);
    }, []);
    const handleClose = useCallback(() => {
        setFormData(EMPTY_MEASURE_FORM);
        setIsStep2QueryValid(false);
        setDrafts([]);
        setDraftErrors([]);
        setEditingDraftIndex(null);
        onClose();
    }, [onClose]);
    const handleCreate = useCallback(async () => {
        if (!formData.name || !formData.calculationType) {
            return;
        }
        const measure = {
            name: formData.name,
            description: formData.description,
            calculationType: formData.calculationType,
            sql: formData.sql,
            measureFunction: formData.measureFunction,
            dimension: formData.dimension,
            type: formData.type || 'number',
        };
        await onSave(measure);
        handleClose();
    }, [formData, onSave, handleClose]);
    const handleEditDraft = useCallback((index) => {
        setFormData(drafts[index]);
        setEditingDraftIndex(index);
        setIsStep2QueryValid(false);
    }, [drafts]);
    const handleRemoveDraft = useCallback((index) => {
        setDrafts((prev) => prev.filter((_draft, position) => position !== index));
        setDraftErrors((prev) => prev.filter((_errors, position) => position !== index));
        setEditingDraftIndex((prev) => {
            if (prev === null || prev < index) {
                return prev;
            }
            return prev === index ? null : prev - 1;
        });
    }, []);
    const handleAddAnother = useCallback(() => {
        if (!currentDraft) {
            return;
        }
        setDrafts((prev) => foldAtIndex(prev, currentDraft, editingDraftIndex));
        setDraftErrors((prev) => (editingDraftIndex === null ? prev : foldAtIndex(prev, [], editingDraftIndex)));
        setEditingDraftIndex(null);
        setFormData(EMPTY_MEASURE_FORM);
        setIsStep2QueryValid(false);
        goToStep(1);
    }, [currentDraft, editingDraftIndex, goToStep]);
    const handleCreateMany = useCallback(async () => {
        if (!onSaveMany) {
            return;
        }
        const pending = currentDraft ? foldAtIndex(drafts, currentDraft, editingDraftIndex) : drafts;
        const { valid, failed } = validateMeasureDrafts(pending, dataView, translate);
        if (failed.length > 0) {
            // Log rejected drafts for troubleshooting/auditing (SSP-4375)
            console.error('Create measures: drafts failed validation:', failed);
        }
        if (valid.length > 0) {
            try {
                await onSaveMany(valid);
            }
            catch (error) {
                // API error: toast already shown by the hook; keep the drafts so the user can retry.
                return;
            }
        }
        if (failed.length === 0) {
            handleClose();
            return;
        }
        // Partial success: only the rejected drafts stay in the wizard, with their reasons.
        setDrafts(failed.map((failure) => pending[failure.index]));
        setDraftErrors(failed.map((failure) => failure.errors));
        setEditingDraftIndex(null);
        setFormData(EMPTY_MEASURE_FORM);
        setIsStep2QueryValid(false);
        goToStep(1);
    }, [onSaveMany, currentDraft, drafts, editingDraftIndex, dataView, translate, handleClose, goToStep]);
    const continueButton = {
        label: translate('Continue'),
        onClick: handleNext,
        disabled: !isReadOnly && currentStep === 1 && !isStep1Valid,
    };
    const createButton = {
        label: isEditMode ? translate('Save') : translate('Create'),
        onClick: handleCreate,
        disabled: isLoading || !formData.name || !formData.calculationType || !isStep2QueryValid,
    };
    const pendingCount = drafts.length + (currentDraft && editingDraftIndex === null ? 1 : 0);
    const hasQueue = canQueueMany && drafts.length > 0;
    const isEditingQueuedDraft = editingDraftIndex !== null;
    // An unfinished edit must never fall through to the queued copy it replaces.
    const hasUnresolvedEdit = isEditingQueuedDraft && !currentDraft;
    const createManyButton = {
        label: translate('Create measures ({count})', { count: pendingCount }),
        onClick: handleCreateMany,
        disabled: isLoading || pendingCount === 0 || hasUnresolvedEdit,
    };
    const addAnotherButton = {
        label: translate('Add another'),
        iconName: 'add',
        iconPosition: 'start',
        onClick: handleAddAnother,
        disabled: !currentDraft,
    };
    // While a queued measure is open, finishing that edit is the action the user is mid-way
    // through, so it takes the primary slot and the batch commit steps back to a secondary.
    const updateDraftButton = {
        label: translate('Update measure'),
        onClick: handleAddAnother,
        disabled: !currentDraft,
    };
    const getPrimaryActionButton = () => {
        if (isReadOnly && isLastStep) {
            return undefined;
        }
        if (!isLastStep) {
            return continueButton;
        }
        if (isEditingQueuedDraft) {
            return updateDraftButton;
        }
        return hasQueue ? createManyButton : createButton;
    };
    const getSecondaryActionButtons = () => {
        const closeOrCancelLabel = isReadOnly ? translate('Close') : translate('Cancel');
        const cancelButton = { label: closeOrCancelLabel, onClick: handleClose };
        if (isFirstStep) {
            // "Add another" resets the details form, so the queue needs a commit action here too.
            return hasQueue ? [cancelButton, createManyButton] : [cancelButton];
        }
        const backButton = { label: translate('Back'), onClick: handlePrevious };
        if (isEditingQueuedDraft) {
            return [cancelButton, backButton, createManyButton];
        }
        if (canQueueMany) {
            return [cancelButton, backButton, addAnotherButton];
        }
        return [cancelButton, backButton];
    };
    const getModalTitle = () => {
        if (isReadOnly) {
            return translate('View measure');
        }
        return isEditMode || isEditingQueuedDraft ? translate('Edit measure') : translate('Create measure');
    };
    if (!isOpen) {
        return null;
    }
    return (_jsxs(ModalWindow, { title: getModalTitle(), subtitle: dataView.name, isOpen: true, closeWindow: handleClose, footerPrimaryActionButton: getPrimaryActionButton(), footerSecondaryActionButtons: getSecondaryActionButtons(), footerCenterContent: _jsx(StepFooter, { currentStep: currentStep, totalSteps: TOTAL_STEPS }), width: 'full', children: [currentStep === 1 && (_jsx(AddEditMeasureStep1, { formData: formData, onFormChange: handleFormChange, isEditMode: isEditMode, isReadOnly: isReadOnly, nameError: nameError, drafts: displayDrafts, draftErrors: draftErrors, draftKeys: draftKeys, editingDraftIndex: editingDraftIndex, onEditDraft: handleEditDraft, onRemoveDraft: handleRemoveDraft })), currentStep === 2 && (_jsx(AddEditMeasureStep2, { formData: formData, onFormChange: handleFormChange, dataView: dataView, isEditMode: isEditMode, isReadOnly: isReadOnly, onQueryValidationChange: handleQueryValidationChange }))] }));
};
